/**
 * Browser autoplay helpers for cinematic intro / cold loads.
 * Catalog clicks usually carry transient user activation; refresh/deep links do not.
 *
 * Browser policy (Chrome/Safari/Firefox):
 * - Unmuted autoplay requires a user gesture (or strong media engagement).
 * - Muted autoplay is generally allowed.
 * - After muted autoplay, unmute MUST run inside the user-gesture call stack.
 *   Deferring unmute with setTimeout(0) loses activation and Chrome keeps the
 *   media effectively silent even if video.muted is set to false.
 *
 * Branch B (refresh / hard reload): Prefer requesting unmuted so Chrome MEI
 * (desktop, origin engagement) can allow audio when the browser permits it.
 * Reload detection alone does NOT guarantee audible autoplay — when play() is
 * rejected, mute-retry + "Tap anywhere for sound" is the compliant path.
 * Do not force muted=false after a browser rejection.
 */

var RELOAD_HINT_KEY = "ngp_nav_reload_hint";
/** Cached for the lifetime of this JS realm (one document load). */
var _reloadNavCached = null;

/**
 * Call from pagehide/beforeunload on the playback host so a subsequent load of
 * the same document can treat the navigation as a reload even when the
 * Navigation Timing API is unavailable or mis-reports type.
 */
export function markPlaybackReloadHint() {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(RELOAD_HINT_KEY, String(Date.now()));
    }
  } catch (e) {
    /* ignore */
  }
}

/**
 * True when a prior playback page left a reload hint (does not consume).
 * @returns {boolean}
 */
function peekPlaybackReloadHint() {
  try {
    if (typeof sessionStorage === "undefined") return false;
    return sessionStorage.getItem(RELOAD_HINT_KEY) != null;
  } catch (ePeek) {
    return false;
  }
}

/**
 * True when a prior playback page left a reload hint (consumed once).
 * @returns {boolean}
 */
function consumePlaybackReloadHint() {
  try {
    if (typeof sessionStorage === "undefined") return false;
    var v = sessionStorage.getItem(RELOAD_HINT_KEY);
    if (v == null) return false;
    sessionStorage.removeItem(RELOAD_HINT_KEY);
    return true;
  } catch (e2) {
    return false;
  }
}

/**
 * Read PerformanceNavigationTiming.type (or legacy numeric equivalent) without
 * mutating the reload cache / hint.
 * @returns {string|null} "reload"|"navigate"|"back_forward"|"prerender"|null
 */
export function getNavigationTimingKind() {
  try {
    if (typeof performance !== "undefined" && performance.getEntriesByType) {
      var entries = performance.getEntriesByType("navigation");
      var nav = entries && entries[0];
      if (nav && nav.type != null && nav.type !== "") {
        var raw = String(nav.type);
        // Some WebViews historically exposed numeric TYPE_* values.
        if (raw === "1") return "reload";
        if (raw === "0") return "navigate";
        if (raw === "2") return "back_forward";
        return raw;
      }
    }
  } catch (e) {
    /* ignore */
  }
  try {
    if (
      typeof performance !== "undefined" &&
      performance.navigation &&
      typeof performance.navigation.type === "number"
    ) {
      var t = performance.navigation.type;
      if (t === 1) return "reload";
      if (t === 2) return "back_forward";
      if (t === 0) return "navigate";
    }
  } catch (e2) {
    /* ignore */
  }
  return null;
}

/**
 * @returns {boolean}
 */
function isReloadTimingKind(kind) {
  return kind === "reload";
}

/**
 * Test helper — clears the per-document reload cache so cases can be re-run
 * in the same realm. Not used by production Host/Player paths.
 */
export function resetReloadNavigationCache() {
  _reloadNavCached = null;
}

/**
 * True when this document load is a browser reload (F5 / Ctrl+R / hard refresh)
 * or when a same-tab playback reload hint was left on pagehide/beforeunload.
 *
 * Caching rules:
 * - Positive reload → cache true (stable for Host config builds).
 * - Definitive non-reload nav type → cache false only after checking the
 *   pagehide hint (Performance API can mis-report; hint wins).
 * - Inconclusive (no navigation entry yet) → do NOT cache false; a later
 *   call may see the entry or the hint. Caching false early made refresh
 *   look like a cold deep link and forced muted intro.
 *
 * @returns {boolean}
 */
export function isReloadNavigation() {
  if (_reloadNavCached === true) {
    return true;
  }
  if (_reloadNavCached === false) {
    return false;
  }

  var navType = getNavigationTimingKind();

  if (isReloadTimingKind(navType)) {
    consumePlaybackReloadHint();
    _reloadNavCached = true;
    return true;
  }

  // pagehide/beforeunload hint — covers mis-reported / delayed Timing on refresh.
  if (peekPlaybackReloadHint() || consumePlaybackReloadHint()) {
    consumePlaybackReloadHint();
    _reloadNavCached = true;
    return true;
  }

  if (navType && !isReloadTimingKind(navType)) {
    // Definitive non-reload (navigate / back_forward / prerender).
    _reloadNavCached = false;
    return false;
  }

  // Inconclusive — do not cache. Caller may retry after navigation entry exists.
  return false;
}

export function hasTransientUserActivation() {
  if (typeof navigator === "undefined") {
    return false;
  }
  try {
    var ua = navigator.userActivation;
    if (ua && typeof ua.isActive === "boolean") {
      return !!ua.isActive;
    }
  } catch (e) {
    /* ignore */
  }
  return false;
}

/**
 * Whether cinematic intro should start muted so autoplay is allowed without a gesture.
 * Mute when there is no usable user activation. Do NOT special-case reload here —
 * hard refresh is handled by shouldForceMutedIntro so media-engagement unmuted
 * autoplay remains possible.
 * @returns {boolean}
 */
export function shouldMuteIntroForAutoplay() {
  return !hasTransientUserActivation();
}

/**
 * Decide whether the cinematic intro config should feed muted:true.
 *
 * Catalog SPA navigations with a live gesture → unmuted.
 * Hard refresh / reload → prefer unmuted start so browsers with media
 * engagement can play intro audio; NextGenPlayer mute-retries if blocked.
 * Cold deep links (no route state) → muted for reliable autoplay, then
 * gesture unmute via the existing host arm.
 *
 * CRITICAL: Do not treat navigation.type === "reload" as an unconditional
 * mute lock — that silenced intros even when the browser would allow audio.
 *
 * CRITICAL: Missing location.state alone is not proof of a cold link. Refresh
 * clears route state. If Navigation Timing is still inconclusive, prefer
 * unmuted (mute-retry remains the browser-policy fallback) instead of the
 * cold-link mute path.
 *
 * @param {object} [opts]
 * @param {boolean} [opts.hasRouteVideoState]
 * @param {boolean} [opts.isReload] inject for tests
 * @param {string|null} [opts.navigationTimingKind] inject for tests
 * @returns {boolean}
 */
export function shouldForceMutedIntro(opts) {
  opts = opts || {};
  var reload =
    typeof opts.isReload === "boolean" ? opts.isReload : isReloadNavigation();
  // Hard refresh: try unmuted first (MEI / prior engagement). Player falls
  // back to muted autoplay + "Tap anywhere for sound" if play() is blocked.
  if (reload) {
    return false;
  }
  if (!opts.hasRouteVideoState) {
    var kind =
      opts.navigationTimingKind !== undefined
        ? opts.navigationTimingKind
        : getNavigationTimingKind();
    // Inconclusive Timing + cleared route state must not become cold-link mute.
    // Genuine cold navigations report type "navigate" (or back_forward).
    if (kind == null || kind === "" || kind === "inconclusive") {
      return false;
    }
    return true;
  }
  return shouldMuteIntroForAutoplay();
}

/**
 * Try to restore audio after a successful muted autoplay.
 * Returns true if unmute appears to have applied; false if browser blocked it.
 *
 * CRITICAL: Never unmute without a live user activation. Doing so after reload
 * (hard refresh) can cause the browser to pause muted-autoplay media — which
 * looks like "intro did not autoplay."
 *
 * @param {{ setMuted: function(boolean): void, getState?: function(): object, getVideoElement?: function(): HTMLVideoElement }} engine
 * @returns {boolean}
 */
export function attemptRestoreAudio(engine) {
  if (!engine || typeof engine.setMuted !== "function") {
    return false;
  }
  if (!hasTransientUserActivation()) {
    return false;
  }
  try {
    applyUnmuteToEngine(engine);
    var st = engine.getState && engine.getState();
    var video =
      typeof engine.getVideoElement === "function"
        ? engine.getVideoElement()
        : null;
    if (st && st.muted === false && (!video || video.muted === false)) {
      return true;
    }
  } catch (e) {
    /* ignore */
  }
  return false;
}

/**
 * Force-unmute engine state + underlying HTMLVideoElement (intro progressive).
 * Clears defaultMuted / muted attribute which can sticky-mute after autoplay.
 * @param {object} engine
 * @param {number} [volume]
 */
export function applyUnmuteToEngine(engine, volume) {
  if (!engine) return;
  if (typeof engine.setMuted === "function") {
    engine.setMuted(false);
  }
  if (
    volume != null &&
    typeof engine.setVolume === "function"
  ) {
    engine.setVolume(volume);
  }
  var video =
    typeof engine.getVideoElement === "function"
      ? engine.getVideoElement()
      : null;
  if (video) {
    try {
      video.muted = false;
      video.defaultMuted = false;
      video.removeAttribute("muted");
      if (volume != null && isFinite(volume)) {
        video.volume = Math.max(0, Math.min(1, volume));
      } else if (video.volume === 0) {
        video.volume = 1;
      }
    } catch (e) {
      /* ignore */
    }
  }
}

/**
 * True when the event target is player chrome that manages mute itself.
 * @param {Event} ev
 * @returns {boolean}
 */
function isMuteControlEvent(ev) {
  var t = ev && ev.target;
  if (!t || typeof t.closest !== "function") return false;
  return !!(
    t.closest("[data-ngp-mute]") ||
    t.closest(".ngp-volume") ||
    t.closest('button[aria-label="Mute"]') ||
    t.closest('button[aria-label="Unmute"]')
  );
}

/**
 * Arm a one-shot unmute on the next pointer/key gesture within `root`.
 *
 * CRITICAL: unmute runs synchronously inside the gesture handler so the
 * browser still has transient user activation (required after muted autoplay).
 *
 * Clicks on the mute control are ignored here so ControlBar toggle does not
 * race (arm unmute → toggle remute). Mute control should call unlock itself.
 *
 * @param {HTMLElement|null} root
 * @param {function(Event=): void} unmuteFn
 * @returns {function(): void} dispose
 */
export function armUnmuteOnNextUserGesture(root, unmuteFn) {
  if (!root || typeof unmuteFn !== "function") {
    return function () {};
  }
  var done = false;
  function fire(ev) {
    if (done) return;
    if (isMuteControlEvent(ev)) {
      return;
    }
    done = true;
    cleanup();
    try {
      unmuteFn(ev);
    } catch (e) {
      /* ignore */
    }
  }
  function cleanup() {
    root.removeEventListener("pointerdown", fire, true);
    root.removeEventListener("keydown", fire, true);
    root.removeEventListener("touchstart", fire, true);
  }
  root.addEventListener("pointerdown", fire, true);
  root.addEventListener("keydown", fire, true);
  root.addEventListener("touchstart", fire, true);
  return cleanup;
}

export default hasTransientUserActivation;
